import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Motion";
import { LINKS } from "../lib/content";

const SECTIONS = [
  {
    label: "Problem",
    body: [
      "Product and GTM leaders at Google had no shortage of behavioral data — the challenge was the opposite: user pain points and product friction were buried inside datasets too large and too raw for executive decision-making. Leaders needed defensible, decision-ready answers, not more dashboards.",
    ],
  },
  {
    label: "Approach",
    body: [
      "As Senior Research Associate, I led consultative research initiatives that started from the leadership question, not the dataset. Large-scale data diagnostics were distilled into executive-ready narratives: what users struggle with, what it costs, and what to build next — packaged with KPI reporting frameworks so the insight didn't die after one presentation but became a repeatable reporting cadence.",
    ],
  },
  {
    label: "Outcome",
    body: [
      "The work influenced 25 product roadmap and GTM decisions and shortened reporting cycles by 35% — analysis that leaders acted on, at a pace that matched their decision calendar.",
    ],
  },
];

const STACK = ["SQL", "Looker Studio", "Tableau", "Google Analytics", "Gong"];

const HeroSvg = () => (
  <svg viewBox="0 0 800 340" xmlns="http://www.w3.org/2000/svg" role="img"
    aria-label="Funnel from large-scale behavioral data to executive decision, with a KPI reporting cadence loop"
    style={{ width: "100%", height: "auto", display: "block" }}>
    <defs>
      <linearGradient id="g-funnel" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#3B82F6" stopOpacity="0.6" />
        <stop offset="1" stopColor="#8B5CF6" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="g-decision" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#8B5CF6" stopOpacity="0.95" />
        <stop offset="1" stopColor="#3B82F6" stopOpacity="0.95" />
      </linearGradient>
    </defs>
    {/* Left: large-scale data cloud */}
    <g>
      <rect x="30" y="80" width="220" height="160" rx="14" fill="none" stroke="#3B82F6" strokeOpacity="0.4" />
      <text x="42" y="102" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill="#3B82F6">LARGE-SCALE BEHAVIORAL DATA</text>
      {/* Data specks */}
      {Array.from({ length: 42 }).map((_, i) => {
        const cx = 50 + (i % 7) * 26 + (i % 2 ? 8 : 0);
        const cy = 122 + Math.floor(i / 7) * 18;
        return <circle key={i} cx={cx} cy={cy} r={i % 5 === 0 ? 2.5 : 1.5} fill="#3B82F6" opacity={0.35 + (i % 5) * 0.13} />;
      })}
    </g>
    {/* Funnel: trapezoid narrowing */}
    <polygon points="270,90 500,140 500,180 270,230" fill="url(#g-funnel)" opacity="0.85" />
    <text x="385" y="128" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill="#fff" opacity="0.9">CONSULTATIVE</text>
    <text x="385" y="145" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill="#fff" opacity="0.9">RESEARCH</text>
    <text x="385" y="175" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontSize="13" fill="#fff" opacity="0.95">executive-ready narrative</text>
    {/* Executive decision node */}
    <g>
      <rect x="520" y="130" width="240" height="60" rx="14" fill="url(#g-decision)" />
      <text x="540" y="155" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill="#fff" opacity="0.9">EXECUTIVE DECISION</text>
      <text x="540" y="176" fontFamily="ui-sans-serif, system-ui" fontSize="14" fontWeight="600" fill="#fff">roadmap · GTM</text>
    </g>
    {/* KPI cadence loop */}
    <path d="M760 175 Q800 250, 640 275 Q480 300, 320 275 Q160 250, 200 200 L250 200" fill="none" stroke="#8B5CF6" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.75" markerEnd="url(#loop-arrow)" />
    <text x="440" y="298" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill="#8B5CF6">KPI REPORTING CADENCE · repeatable</text>
    <defs>
      <marker id="loop-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
        <path d="M0 0 L10 5 L0 10 z" fill="#8B5CF6" />
      </marker>
    </defs>
  </svg>
);

export default function GoogleExecutiveAnalytics() {
  useEffect(() => { document.title = "Executive Analytics & Product Strategy at Google - Yash Bhatt"; }, []);
  return (
    <main data-testid="google-executive-analytics-page" className="pt-16">
      <header className="relative overflow-hidden border-b border-border">
        <div className="mesh-glow w-[500px] h-[500px] bg-violet-600/15 -top-40 -right-32" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <li><Link to="/" className="hover:text-secondary transition-colors duration-200">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/projects" className="hover:text-secondary transition-colors duration-200">Projects</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-secondary">Executive Analytics at Google</li>
            </ol>
          </nav>
          <MaskedLine>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-secondary mb-5">Consultative Research · Google</p>
          </MaskedLine>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.15] pb-2">
            <MaskedLine delay={0.15}>Executive Analytics & <span className="text-gradient">Product Strategy at Google</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="mt-6 font-display text-lg text-gradient font-medium">25 roadmap & GTM decisions influenced · 35% faster reporting cycles</p>
          </MaskedLine>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <Reveal className="mb-14">
          <figure className="rounded-2xl border border-border bg-card/40 p-5 md:p-8" data-testid="case-hero-svg">
            <HeroSvg />
            <figcaption className="mt-4 text-xs font-mono text-muted-foreground">From large-scale data to decisions leaders act on — packaged into a repeatable cadence.</figcaption>
          </figure>
        </Reveal>

        {SECTIONS.map((s, si) => (
          <Reveal key={s.label} delay={si * 0.05} className="mb-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-5">{s.label}</p>
            {s.body.map((para, pi) => (
              <p key={pi} className="text-base md:text-lg text-foreground/85 leading-relaxed mb-5">{para}</p>
            ))}
          </Reveal>
        ))}

        <Reveal className="mt-12 flex flex-wrap gap-2">
          {STACK.map((t) => (
            <span key={t} className="px-3 py-1 rounded-full border border-border text-xs text-muted-foreground">{t}</span>
          ))}
        </Reveal>

        <Reveal className="mt-16 pt-10 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <Link to="/projects" data-testid="case-back-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
            <ArrowLeft size={14} /> All case studies
          </Link>
          <a href={LINKS.calendly} target="_blank" rel="noreferrer" data-testid="case-cta"
            className="group inline-flex h-12 items-center gap-2 px-7 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200">
            Book a Consultation <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </article>
    </main>
  );
}
